import { useEffect, useMemo, useRef, useState } from 'react';
import { useQueryClient, type QueryClient } from '@tanstack/react-query';
import { WsClient, type WsClientOptions, type WsStatus } from './socket';
import type { WsEvent } from './events';

/**
 * The query keys a WS event invalidates. Each SPA passes its own map, because the keys are the
 * SPA's — this package must not hard-code the buyer's `['cart']` and then be wrong in the admin.
 *
 * Invalidation, NOT cache-writing, is the rule: the frame tells us *that* something changed; the
 * value the user sees is always re-fetched through the audience-filtered REST mapper.
 */
export type WsInvalidationMap = Record<string, readonly (readonly unknown[])[]>;

export interface UseWsOptions extends WsClientOptions {
  /** Skip connecting entirely — e.g. while `GET /me` says nobody is logged in. */
  enabled?: boolean;
  /** Event type → query keys to invalidate. */
  invalidate?: WsInvalidationMap;
  /** Escape hatch for events that need more than an invalidation (a toast, say). */
  onEvent?: (event: WsEvent, queryClient: QueryClient) => void;
}

export interface UseWsResult {
  client: WsClient;
  /** Live connection state, for the admin's "counters may be stale" indicator. */
  status: WsStatus;
}

/**
 * Opens the socket for the lifetime of the component and bridges frames into TanStack Query.
 * Mount it ONCE, in the app shell.
 */
export function useWs({ enabled = true, invalidate = {}, onEvent, ...socketOptions }: UseWsOptions = {}): UseWsResult {
  const queryClient = useQueryClient();

  const client = useMemo(
    () => new WsClient(socketOptions),
    // The socket options are static per app; re-creating the client on every render would
    // reconnect on every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  const [status, setStatus] = useState<WsStatus>(() => client.getStatus());

  // Keep the latest handlers without tearing the socket down when they change identity. This
  // must be a real ref, NOT a useMemo keyed on the handlers: callers pass inline closures, so
  // a memo would change identity on every render, and with the connect effect depending on it
  // each render would disconnect/reconnect the socket — a status change re-renders, which
  // re-dials, which changes status: a self-sustaining reconnect storm.
  const handlersRef = useRef({ invalidate, onEvent });
  useEffect(() => {
    handlersRef.current = { invalidate, onEvent };
  });

  useEffect(() => {
    // Status is mirrored into React state for the whole lifetime of the client, not just while
    // enabled — otherwise flipping `enabled` to false would leave the indicator frozen on the
    // last value it happened to see instead of reporting `closed`.
    const unsubscribeStatus = client.subscribeStatus(setStatus);

    if (!enabled) {
      client.disconnect();
      return unsubscribeStatus;
    }

    const unsubscribe = client.subscribe((event) => {
      const keys = handlersRef.current.invalidate[event.type];
      if (keys) {
        for (const queryKey of keys) {
          void queryClient.invalidateQueries({ queryKey });
        }
      }
      handlersRef.current.onEvent?.(event, queryClient);
    });

    client.connect();

    return () => {
      unsubscribe();
      unsubscribeStatus();
      client.disconnect();
    };
  }, [client, enabled, queryClient]);

  return { client, status };
}
